import { useState, useRef, useEffect } from 'react';
import { useRex } from '../context/RexContext';

const REX_ICON = (
  <svg width="13" height="13" viewBox="0 0 40 40" aria-hidden>
    <polygon points="20,3 23.5,16.5 37,20 23.5,23.5 20,37 16.5,23.5 3,20 16.5,16.5" fill="#534AB7" />
    <polygon points="20,10 22.2,17.8 30,20 22.2,22.2 20,30 17.8,22.2 10,20 17.8,17.8" fill="#EEEDFE" />
  </svg>
);

function RexMessage({ role, text }) {
  if (role === 'user') {
    return <div className="rex-user-bubble">{text}</div>;
  }
  return <div className="rex-bubble">{text}</div>;
}

function RexWorkingDots() {
  return (
    <div className="rex-panel-status" aria-label="Rex is working">
      <span className="rex-working-dot" />
      <span className="rex-working-dot" />
      <span className="rex-working-dot" />
    </div>
  );
}

/**
 * Persistent Rex tab + slide-out panel. User-initiated only.
 */
export default function RexPanel() {
  const {
    contextLabel, suggestions, messages, loading, streamingText, error, priming, apply,
    charterDrafting, charterDraftComplete,
    sendMessage, hasSuggestions, panelOpenSignal, panelCloseSignal,
  } = useRex();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const streamRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open, streamingText, charterDrafting, charterDraftComplete]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [streamingText]);

  useEffect(() => {
    if (panelOpenSignal) setOpen(true);
  }, [panelOpenSignal]);

  useEffect(() => {
    if (panelCloseSignal) setOpen(false);
  }, [panelCloseSignal]);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    await sendMessage(text);
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const showPriming = messages.length === 0 && !loading && !charterDrafting;
  const primed = hasSuggestions && !open;
  const lastMessage = messages[messages.length - 1];
  const canApply = apply?.enabled && lastMessage?.role === 'assistant' && !loading;
  const showCharterDrafting = charterDrafting && !charterDraftComplete;
  const showHeaderWorking = loading || showCharterDrafting;

  return (
    <div className={`rex-rail${open ? ' open' : ''}`}>
      {!open && (
        <button
          type="button"
          className={`rex-tab${primed ? ' primed' : ''}`}
          onClick={() => setOpen(true)}
          aria-label="Open Rex assistant"
        >
          {primed && <span className="rex-badge" aria-hidden />}
          <div className="rex-tab-avatar">{REX_ICON}</div>
          <div className="rex-tab-label">Rex</div>
        </button>
      )}

      {open && (
        <div className="rex-panel open">
          <div className="rex-panel-header">
            <div className="rex-panel-avatar">{REX_ICON}</div>
            <span className="rex-panel-name">Rex</span>
            {showHeaderWorking && <RexWorkingDots />}
            <button type="button" className="rex-close-btn" onClick={() => setOpen(false)} aria-label="Close Rex">
              ✕
            </button>
          </div>

          <div className="rex-panel-scroll" ref={scrollRef}>
            <div className="rex-context">
              <span className="rex-context-dot" />
              {contextLabel}
            </div>

            {showPriming && <div className="rex-bubble">{priming}</div>}

            {messages.map((m, i) => {
              const isLastAssistant = i === messages.length - 1 && m.role === 'assistant';
              if (showCharterDrafting && isLastAssistant && loading) return null;
              return <RexMessage key={`${m.role}-${i}`} role={m.role} text={m.content} />;
            })}

            {showCharterDrafting && (
              <div className="rex-draft-status">
                <div className="rex-bubble">
                  <div className="rex-draft-title">Drafting your charter…</div>
                  <div className="rex-draft-sub">Using your brief + platform knowledge base</div>
                </div>
                {loading && (
                  <>
                    <div className="rex-stream-label">Rex is writing…</div>
                    <div className="rex-stream-text" ref={streamRef}>
                      {streamingText || '\u00A0'}
                    </div>
                  </>
                )}
                {!loading && (
                  <div className="rex-bubble muted">Structuring fields for the form…</div>
                )}
              </div>
            )}

            {charterDraftComplete && (
              <div className="rex-bubble rex-draft-complete">
                Charter drafted — review and edit freely.
              </div>
            )}

            {loading && !charterDrafting && messages.length > 0 && (
              <div className="rex-bubble muted">Thinking…</div>
            )}

            {error && <div className="rex-bubble muted" style={{ color: '#A32D2D' }}>{error}</div>}

            {showPriming && suggestions.length > 0 && (
              <div className="rex-suggestions">
                <div className="rex-suggestion-label">Suggestions</div>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="rex-suggestion primary"
                    onClick={() => sendMessage(s)}
                    disabled={loading}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rex-panel-footer">
            {canApply && (
              <div className="rex-apply-bar">
                <button
                  type="button"
                  className="rex-btn primary"
                  style={{ width: '100%' }}
                  disabled={loading}
                  onClick={() => apply.onApply(messages)}
                >
                  {apply.label || 'Apply to screen'}
                </button>
              </div>
            )}
            <textarea
              ref={inputRef}
              className="rex-input"
              rows={3}
              placeholder="Ask Rex about this screen…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Message Rex"
            />
            <div className="rex-actions">
              {suggestions.length > 0 && (
                <button
                  type="button"
                  className="rex-btn muted"
                  disabled={loading}
                  onClick={() => sendMessage(suggestions[0])}
                >
                  Suggest
                </button>
              )}
              <button
                type="button"
                className="rex-btn primary"
                onClick={handleSend}
                disabled={loading || !input.trim()}
              >
                {loading ? '…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
